import { Button, CircularProgress } from '@material-ui/core';
import { KeyIcon, TrashIcon, UserMinusIcon } from '@phosphor-icons/react';
import { Layout } from '@orientaction/components';
import { useCompany } from '@orientaction/hooks';
import { getFromLS, valueAliasRole } from '@orientaction/utils';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import GeneratedPasswordModal from '../BeneficiaryProfile/components/GeneratedPasswordModal';
import ConfirmActionModal from '../shared/components/ConfirmActionModal';
import { CONSULTANT_ROLE_ID } from '../shared/constants';
import { readRole, unwrapCollection } from '../shared/utils';
import BeneficiariesTab from './components/BeneficiariesTab';
import HierarchyUsersTab from './components/HierarchyUsersTab';
import MinorTestsTab from './components/MinorTestsTab';
import PrestationsTab from './components/PrestationsTab';
import ProfileTab from './components/ProfileTab';
import AddConsultantPrestationModal from './components/AddConsultantPrestationModal';
import {
  buildDeletePrestationText,
  DELETE_PRESTATION_CONFIRM,
  DELETE_PRESTATION_TITLE,
  DELETE_USER_CONFIRM,
  DELETE_USER_TEXT,
  DELETE_USER_TITLE,
  DISABLE_USER_CONFIRM,
  DISABLE_USER_TEXT,
  DISABLE_USER_TITLE,
  ENABLE_USER_CONFIRM,
  ENABLE_USER_TEXT,
  ENABLE_USER_TITLE,
  GENERATED_PASSWORD_TEXT,
  GENERATED_PASSWORD_TITLE,
  getBackLabel,
  getHierarchyAttachedTabs,
  getProfileTabs,
  isDirectReassignTab,
  STATUS_ACTIVE,
  STATUS_INACTIVE,
} from './constants';
import { useStyles } from './styles';
import { useConsultantProfile } from './useConsultantProfile';

const ConsultantProfile: FC = () => {
  const classes = useStyles();
  const page = useConsultantProfile();
  const { companyUserRole, companies } = useCompany();
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const companyId = page.user?.company?.id || userConnected?.company?.id;
  const alias = companies.find((item: any) => Number(item.id) === Number(companyId));

  const role = readRole(page.user?.company_user_role);
  const roleLabel = useMemo(() => {
    if (!role) {
      return '';
    }
    const fromList = unwrapCollection(companyUserRole).find(
      (item: any) => Number(item.id) === Number(role.id)
    );
    const rawName = fromList?.attributes?.name || fromList?.name || role.name || '';
    return valueAliasRole(rawName, alias?.attributes?.companyUserRoleAlias) || rawName || 'Consultant';
  }, [role, companyUserRole, alias]);

  const fullName =
    `${page.user?.firstname || ''} ${page.user?.lastname || ''}`.trim() || roleLabel || 'Consultant';
  const isActive = Boolean(page.user?.isActivated);
  const profileTabs = useMemo(
    () => getProfileTabs(page.roleId, alias?.attributes?.companyUserRoleAlias),
    [page.roleId, alias]
  );
  const hierarchyTabs = useMemo(
    () => getHierarchyAttachedTabs(page.roleId, alias?.attributes?.companyUserRoleAlias),
    [page.roleId, alias]
  );
  const activeHierarchyTab = hierarchyTabs.find(item => item.id === page.tab);
  const isConsultant = page.roleId === CONSULTANT_ROLE_ID;
  const confirmCopy =
    page.confirmModal.type === 'delete'
      ? { title: DELETE_USER_TITLE, text: DELETE_USER_TEXT, confirmLabel: DELETE_USER_CONFIRM }
      : page.confirmModal.type === 'enable'
      ? { title: ENABLE_USER_TITLE, text: ENABLE_USER_TEXT, confirmLabel: ENABLE_USER_CONFIRM }
      : { title: DISABLE_USER_TITLE, text: DISABLE_USER_TEXT, confirmLabel: DISABLE_USER_CONFIRM };

  return (
    <Layout isaddBackground={true}>
      <div className={classes.page}>
        <button
          type="button"
          className={classes.backLink}
          onClick={page.editingProfile ? () => page.setEditingProfile(false) : page.goBack}>
          {page.editingProfile ? '← Retour' : getBackLabel(page.roleId)}
        </button>

        {page.loading ? (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {!page.editingProfile && (
              <>
                <div className={classes.titleRow}>
                  <h1 className={classes.title}>{fullName}</h1>
                  {roleLabel ? <span className={classes.roleTag}>{roleLabel}</span> : null}
                  <span
                    className={clsx(
                      classes.statusTag,
                      isActive ? classes.statusActive : classes.statusInactive
                    )}>
                    {isActive ? STATUS_ACTIVE : STATUS_INACTIVE}
                  </span>
                </div>

                <div className={classes.tabs}>
                  {profileTabs.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className={clsx(classes.tab, page.tab === item.id && classes.tabActive)}
                      onClick={() => page.setTab(item.id)}>
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {page.tab === 'profil' && (
              <ProfileTab
                user={page.user}
                editing={page.editingProfile}
                roleLabel={roleLabel}
                editTitle={isConsultant ? undefined : 'Modifier le profil'}
                onStartEdit={() => page.setEditingProfile(true)}
                onUserUpdated={page.fetchUser}
                onEditingChange={page.setEditingProfile}
              />
            )}
            {isConsultant && page.tab === 'prestations' && (
              <PrestationsTab
                user={page.user}
                reloadKey={page.prestationsReloadKey}
                onDeletePrestation={page.askDeletePrestation}
                onAddPrestation={page.goToAddPrestation}
              />
            )}
            {isConsultant && page.tab === 'tests-mineurs' && (
              <MinorTestsTab user={page.user} onSaved={page.fetchUser} />
            )}
            {isConsultant && page.tab === 'beneficiaires' && <BeneficiariesTab user={page.user} />}
            {activeHierarchyTab && (
              <HierarchyUsersTab
                users={page.hierarchy?.[activeHierarchyTab.hierarchyKey] || []}
                attachedRoleId={activeHierarchyTab.roleId}
                attachedLabel={activeHierarchyTab.label}
                user={page.user}
                roleLabel={roleLabel}
                canReassign={isDirectReassignTab(page.roleId, activeHierarchyTab.roleId)}
                loading={page.loadingHierarchy}
                onReassigned={page.reloadHierarchy}
              />
            )}

            {page.tab === 'profil' && !page.editingProfile && (
              <div className={classes.actions}>
                <Button
                  className={classes.actionBtn}
                  startIcon={<KeyIcon size={18} />}
                  disabled={page.generatingPassword}
                  onClick={page.generatePassword}>
                  {page.generatingPassword ? 'Génération...' : 'Générer un mot de passe aléatoire'}
                </Button>
                <Button
                  className={classes.actionBtn}
                  startIcon={<UserMinusIcon size={18} />}
                  onClick={page.askDisable}>
                  {isActive ? "Désactiver l'utilisateur" : "Réactiver l'utilisateur"}
                </Button>
                <Button
                  className={classes.actionBtn}
                  startIcon={<TrashIcon size={18} />}
                  onClick={page.askDelete}>
                  Supprimer définitivement
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <GeneratedPasswordModal
        open={page.passwordModal.open}
        password={page.passwordModal.password}
        title={GENERATED_PASSWORD_TITLE}
        text={GENERATED_PASSWORD_TEXT}
        onClose={page.closePasswordModal}
      />

      <ConfirmActionModal
        open={page.confirmModal.open}
        title={confirmCopy.title}
        text={confirmCopy.text}
        confirmLabel={confirmCopy.confirmLabel}
        confirming={page.confirming}
        onClose={page.closeConfirmModal}
        onConfirm={page.confirmAction}
      />

      <ConfirmActionModal
        open={page.prestationConfirm.open}
        title={DELETE_PRESTATION_TITLE}
        text={buildDeletePrestationText(page.prestationConfirm.service?.name || '')}
        confirmLabel={DELETE_PRESTATION_CONFIRM}
        confirming={page.deletingPrestation}
        onClose={page.closePrestationConfirm}
        onConfirm={page.confirmDeletePrestation}
      />

      <AddConsultantPrestationModal
        open={page.addPrestationOpen}
        saving={page.addingPrestation}
        services={page.availablePrestations}
        onClose={page.closeAddPrestation}
        onSave={page.confirmAddPrestation}
      />
    </Layout>
  );
};

export default ConsultantProfile;
